import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Z_MODAL } from "../constants/zIndex";
import { modalBackdropVariants, modalPanelVariants } from "../constants/motion";

const SIZE_CLASSES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  "2xl": "max-w-4xl",
  "3xl": "max-w-3xl",
  full: "max-w-5xl",
} as const;

const DEFAULT_PANEL_STYLE = "max-h-[90vh] rounded-2xl bg-white shadow-2xl";

type ModalSize = keyof typeof SIZE_CLASSES;

type ModalProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  description?: string;
  size?: ModalSize;
  showCloseButton?: boolean;
  closeLabel?: string;
  closeOnBackdropClick?: boolean;
  /**
   * Fully replaces the default panel visual style (rounded-2xl bg-white
   * shadow-2xl) when provided — pass your own rounded/bg/shadow rather than
   * layering on top of the default, to avoid conflicting Tailwind utilities.
   */
  panelClassName?: string;
  bodyClassName?: string;
  /**
   * false lets the panel manage its own internal scroll regions (e.g. a
   * sticky header/footer around a scrolling body) instead of Modal making
   * the whole panel scroll.
   */
  scrollBody?: boolean;
};

// Tracks how many <Modal> instances are simultaneously open so nested/stacked
// modals don't fight over restoring body scroll when one of them closes.
let openModalCount = 0;

export default function Modal({
  open,
  onClose,
  children,
  title,
  description,
  size = "md",
  showCloseButton = true,
  closeLabel = "Close",
  closeOnBackdropClick = true,
  panelClassName,
  bodyClassName,
  scrollBody = true,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    openModalCount += 1;
    document.body.style.overflow = "hidden";
    return () => {
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Don't steal focus from an autoFocus element inside (e.g. a form
    // input) — the browser already focuses it during commit, before this
    // effect runs. Only claim focus ourselves if nothing inside did.
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      panel.focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // An explicit bodyClassName="" (distinct from the default) means the
  // caller is building its own header/scroll-body/footer layout in children,
  // almost always against a `panelClassName` with its own flex-col. Rendering
  // this wrapper as a normal block div then breaks that: it isn't a flex
  // container, so a child's `flex-1 overflow-y-auto` has no flex parent to
  // size against, grows unbounded, and the panel's own overflow-hidden just
  // clips it — no scrollbar, content cut off. `contents` removes the wrapper
  // from the box tree entirely so its children become real flex items of the
  // panel, and is a no-op for panels that aren't flex-col.
  const resolvedBodyClassName =
    bodyClassName === ""
      ? "contents"
      : (bodyClassName ?? (title || description ? "p-6 pt-4" : "p-6"));
  // size only governs width via the default panel style — a custom
  // panelClassName is expected to specify its own width/max-w too.
  const sizeClass = panelClassName ? "" : SIZE_CLASSES[size];

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className={`fixed inset-0 ${Z_MODAL} flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm`}
          variants={modalBackdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onMouseDown={(event) => {
            if (closeOnBackdropClick && event.target === event.currentTarget) {
              onClose();
            }
          }}
        >
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            tabIndex={-1}
            variants={modalPanelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`relative w-full ${sizeClass} outline-none ${
              scrollBody ? "overflow-y-auto" : "overflow-hidden"
            } ${panelClassName ?? DEFAULT_PANEL_STYLE}`}
          >
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
            {(title || description) && (
              <div className={`px-6 pt-6 ${showCloseButton ? "pr-12" : ""}`}>
                {title && (
                  <h2 id={titleId} className="text-lg font-bold text-gray-900">
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="mt-1 text-sm text-gray-500">{description}</p>
                )}
              </div>
            )}
            <div className={resolvedBodyClassName}>{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
